import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { uploadPromptVideoDirect } from '../Prompts/promptVideoUpload';
import type { PromptVideoUploadPhase } from '../Prompts/promptVideoUploadTracker';

export type CreatePromptVideoUploadState = {
  phase: PromptVideoUploadPhase;
  percent: number;
  error?: string;
};

function fileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

export function reindexNumericRecord<T>(
  source: Record<number, T>,
  removedIndex: number
): Record<number, T> {
  const re: Record<number, T> = {};
  Object.entries(source).forEach(([k, value]) => {
    const oldIdx = parseInt(k, 10);
    if (oldIdx === removedIndex) return;
    const newIdx = oldIdx > removedIndex ? oldIdx - 1 : oldIdx;
    re[newIdx] = value;
  });
  return re;
}

function reindexRefRecord<T>(record: Record<number, T>, removedIndex: number) {
  const re: Record<number, T> = {};
  Object.entries(record).forEach(([k, value]) => {
    const oldIdx = parseInt(k, 10);
    if (oldIdx === removedIndex) return;
    const newIdx = oldIdx > removedIndex ? oldIdx - 1 : oldIdx;
    re[newIdx] = value;
  });
  return re;
}

export function useCreatePromptVideoUploads(videoFiles: Record<number, File>) {
  const [videoUrls, setVideoUrls] = useState<Record<number, string>>({});
  const [videoUploads, setVideoUploads] = useState<Record<number, CreatePromptVideoUploadState>>(
    {}
  );
  const uploadAbortRef = useRef<Record<number, boolean>>({});
  const uploadAbortFnRef = useRef<Record<number, () => void>>({});
  const uploadedFileKeyRef = useRef<Record<number, string>>({});
  const inFlightRef = useRef<Set<number>>(new Set());
  const uploadErrorIndicesRef = useRef<Set<number>>(new Set());
  const videoUrlsRef = useRef(videoUrls);
  videoUrlsRef.current = videoUrls;

  const clearVideoUploadState = useCallback((index: number) => {
    uploadAbortFnRef.current[index]?.();
    delete uploadAbortFnRef.current[index];
    uploadAbortRef.current[index] = true;
    inFlightRef.current.delete(index);
    uploadErrorIndicesRef.current.delete(index);
    delete uploadedFileKeyRef.current[index];
    setVideoUrls((prev) => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setVideoUploads((prev) => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  const clearAllVideoUploadState = useCallback(() => {
    Object.values(uploadAbortFnRef.current).forEach((abort) => abort());
    uploadAbortFnRef.current = {};
    Object.keys(uploadAbortRef.current).forEach((k) => {
      uploadAbortRef.current[parseInt(k, 10)] = true;
    });
    inFlightRef.current.clear();
    uploadErrorIndicesRef.current.clear();
    uploadedFileKeyRef.current = {};
    setVideoUrls({});
    setVideoUploads({});
  }, []);

  const reindexVideoUploadState = useCallback((removedIndex: number) => {
    setVideoUrls((prev) => reindexNumericRecord(prev, removedIndex));
    setVideoUploads((prev) => reindexNumericRecord(prev, removedIndex));
    uploadAbortRef.current = reindexRefRecord(uploadAbortRef.current, removedIndex);
    uploadedFileKeyRef.current = reindexRefRecord(uploadedFileKeyRef.current, removedIndex);
    inFlightRef.current = new Set(
      Array.from(inFlightRef.current)
        .filter((idx) => idx !== removedIndex)
        .map((idx) => (idx > removedIndex ? idx - 1 : idx))
    );
    uploadErrorIndicesRef.current = new Set(
      Array.from(uploadErrorIndicesRef.current)
        .filter((idx) => idx !== removedIndex)
        .map((idx) => (idx > removedIndex ? idx - 1 : idx))
    );
  }, []);

  const uploadVideoAtIndex = useCallback((index: number, file: File) => {
    if (inFlightRef.current.has(index)) {
      return;
    }

    const fk = fileKey(file);
    const abortFns: Array<() => void> = [];
    inFlightRef.current.add(index);
    uploadAbortRef.current[index] = false;
    uploadAbortFnRef.current[index] = () => {
      abortFns.forEach((abort) => abort());
    };

    setVideoUrls((prev) => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setVideoUploads((prev) => ({
      ...prev,
      [index]: { phase: 'presigning', percent: 2 },
    }));

    void uploadPromptVideoDirect(file, {
      registerAbort: (abort) => {
        abortFns.push(abort);
      },
      onProgress: (progress) => {
        if (uploadAbortRef.current[index]) return;
        setVideoUploads((prev) => ({
          ...prev,
          [index]: { phase: progress.phase, percent: progress.percent },
        }));
      },
    })
      .then((publicUrl) => {
        if (uploadAbortRef.current[index]) return;
        uploadedFileKeyRef.current[index] = fk;
        uploadErrorIndicesRef.current.delete(index);
        setVideoUrls((prev) => ({ ...prev, [index]: publicUrl }));
        setVideoUploads((prev) => ({
          ...prev,
          [index]: { phase: 'done', percent: 100 },
        }));
      })
      .catch((err: unknown) => {
        if (uploadAbortRef.current[index]) return;
        const message = err instanceof Error ? err.message : 'Video upload failed.';
        setVideoUploads((prev) => ({
          ...prev,
          [index]: { phase: 'error', percent: 0, error: message },
        }));
        uploadErrorIndicesRef.current.add(index);
        toast.error(message);
      })
      .finally(() => {
        inFlightRef.current.delete(index);
        delete uploadAbortFnRef.current[index];
      });
  }, []);

  const cancelVideoUpload = useCallback((index: number) => {
    uploadAbortRef.current[index] = true;
    uploadAbortFnRef.current[index]?.();
    delete uploadAbortFnRef.current[index];
    inFlightRef.current.delete(index);
    setVideoUploads((prev) => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  const retryVideoUpload = useCallback(
    (index: number) => {
      const file = videoFiles[index];
      if (!file) {
        toast.error('No video file found to retry.');
        return;
      }
      delete uploadedFileKeyRef.current[index];
      uploadErrorIndicesRef.current.delete(index);
      uploadVideoAtIndex(index, file);
    },
    [videoFiles, uploadVideoAtIndex]
  );

  useEffect(() => {
    Object.entries(videoFiles).forEach(([key, file]) => {
      const index = parseInt(key, 10);
      const fk = fileKey(file);
      if (uploadedFileKeyRef.current[index] === fk && videoUrlsRef.current[index]) {
        return;
      }
      if (uploadErrorIndicesRef.current.has(index)) {
        return;
      }
      if (inFlightRef.current.has(index)) {
        return;
      }

      uploadVideoAtIndex(index, file);
    });
  }, [videoFiles, clearVideoUploadState, uploadVideoAtIndex]);

  const hasPendingVideoUploads = useCallback(() => {
    return Object.keys(videoFiles).some((key) => {
      const index = parseInt(key, 10);
      if (videoUrls[index]) return false;
      const upload = videoUploads[index];
      return !upload || upload.phase !== 'done';
    });
  }, [videoFiles, videoUrls, videoUploads]);

  const hasVideoUploadErrors = useCallback(() => {
    return Object.values(videoUploads).some((u) => u.phase === 'error');
  }, [videoUploads]);

  const seedVideoUrls = useCallback((urls: Record<number, string>) => {
    setVideoUrls((prev) => ({ ...prev, ...urls }));
  }, []);

  const replaceVideoUrls = useCallback((urls: Record<number, string>) => {
    uploadedFileKeyRef.current = {};
    inFlightRef.current.clear();
    uploadErrorIndicesRef.current.clear();
    uploadAbortRef.current = {};
    setVideoUploads({});
    setVideoUrls(urls);
  }, []);

  return {
    videoUrls,
    videoUploads,
    clearVideoUploadState,
    clearAllVideoUploadState,
    reindexVideoUploadState,
    retryVideoUpload,
    cancelVideoUpload,
    hasPendingVideoUploads,
    hasVideoUploadErrors,
    seedVideoUrls,
    replaceVideoUrls,
  };
}
